import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Img from 'gatsby-image';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCalendarAlt, faClock} from '@fortawesome/pro-regular-svg-icons';
import format from 'date-fns/format';
import hrLocale from 'date-fns/locale/hr';

import EnvData from '../EnvData';
import Layout from '../Layout';
import {PRIMARY_COLOR, PRIMARY_COLOR_2, TEXT_MUTED1, TEXT_MUTED2} from '../../constants';
import Nav from '../Nav';
import SignUpCTA from '../SignUpCTA';
import Footer from '../Footer';

import blog from '../../../data/blog';
import authors from '../../../data/author';

export default function BlogContainer({id, children = null, authorImage = null, ogImage = null, uri = null}) {
	const {title, description, minutesRead, author: authorId, date: postDate} = blog.find((el) => el.id === id);
	const {name: authorName} = authors.find((el) => el.id === authorId);

	return (
		<EnvData>
			<ThePage
				title={title}
				description={description}
				author={authorName}
				authorImage={authorImage}
				minutesRead={minutesRead}
				ogImage={ogImage}
				uri={uri}
				date={postDate}
			>
				{children}
			</ThePage>
		</EnvData>
	);
}

BlogContainer.propTypes = {
	id: PropTypes.number.isRequired,
	children: PropTypes.node,
	// eslint-disable-next-line
	authorImage: PropTypes.object, // always fixed
	// eslint-disable-next-line
	ogImage: PropTypes.object, // always fixed, should be 1080 in width
	uri: PropTypes.string
};

const ThePage = function ({
	title,
	description = null,
	children = null,
	author = null,
	authorImage = null,
	minutesRead = null,
	ogImage = null,
	uri = null,
	date = null
}) {
	const dt = new Date(Date.parse(date));
	const fmt = format(dt, 'LLLL, yyyy', {
		locale: hrLocale
	});

	return (
		<Layout title={title} description={description} uri={uri} ogImage={ogImage}>
			<Nav background={PRIMARY_COLOR_2} />
			<Article>
				<h1>{title}</h1>
				<About>
					<Img fixed={authorImage} alt={author} width={40} height={40} />
					<span>{author}</span>
					<FontAwesomeIcon icon={faCalendarAlt} />
					<span title={`Objavljeno ${fmt}`}>{fmt}</span>
					<FontAwesomeIcon icon={faClock} />
					<span>{minutesRead} min read</span>
				</About>
				{children}
			</Article>
			<SignUpCTA mt="5rem" />
			<Footer />
		</Layout>
	);
};

ThePage.propTypes = {
	title: PropTypes.string.isRequired,
	description: PropTypes.string,
	children: PropTypes.node,
	author: PropTypes.string,
	// eslint-disable-next-line
	authorImage: PropTypes.object,
	minutesRead: PropTypes.number,
	// eslint-disable-next-line
	ogImage: PropTypes.object,
	uri: PropTypes.string,
	date: PropTypes.string.isRequired
};

const Article = styled.article`
	display: block;
	margin: 0 auto;
	width: 100%;
	max-width: 1300px;
	box-sizing: border-box;
	padding: 0 1rem;

	section {
		position: relative;
	}

	h1 {
		display: block;
		text-align: center;
		line-height: 1.25;
		font-size: 2.75rem;
		font-weight: bold;
		margin: 3rem 0;
		letter-spacing: -1px;
	}

	h2 {
		display: block;
		text-align: center;
		line-height: 1.25;
		font-size: 2.25rem;
		font-weight: bold;
		margin: 2rem 0;
		letter-spacing: -1px;
	}

	h3 {
		display: block;
		text-align: center;
		line-height: 1.25;
		font-size: 1.5rem;
		font-weight: normal;
		margin: 1.5rem 0;
	}

	p,
	ul,
	ol,
	blockquote {
		display: block;
		width: 100%;
		max-width: 800px;
		margin: 1.75rem auto;
		line-height: 1.75;
		font-size: 0.925rem;
		color: #444444;

		a {
			color: ${PRIMARY_COLOR};
			text-decoration: underline;

			&:hover {
				text-decoration: none;
				color: ${PRIMARY_COLOR_2};
			}
		}
	}

	ul,
	ol {
		list-style-position: outside;
		box-sizing: border-box;
		padding-left: 1rem;

		> li {
			line-height: 1.5;
			margin: 0.5rem 0;

			&:first-child {
				margin-top: 0;
			}

			&:last-child {
				margin-bottom: 0;
			}
		}
	}

	blockquote {
		padding: 0.5rem 0 0.5rem 1.5rem;
		border-left: 4px solid #aaaaaa;
		line-height: 1.5;
	}
`;

const About = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 2rem auto;

	> div:first-child {
		// author image wrapper
		border-radius: 100%;
	}

	> span:nth-child(2) {
		// ^^ author name
		margin: 0 1rem;
		font-size: 0.75rem;
		color: ${TEXT_MUTED1};
	}

	> svg {
		margin: 0 0.5rem 0 1rem;
		color: ${TEXT_MUTED2};
		font-size: 0.75rem;
	}

	> span:nth-child(4) {
		color: ${TEXT_MUTED2};
		font-size: 0.75rem;
	}

	> span:nth-child(6) {
		color: ${TEXT_MUTED2};
		font-size: 0.75rem;
	}
`;
